import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Room, RoomParams } from '../../interfaces/api/room';
import { fetchRooms, fetchRoomsSearchFilter, updateRoom } from '../services/rooms.service';
import { RoomSearchFilterParam } from '../../api/rooms';

interface RoomState {
	rooms: {
		[id: number]: Room;
	};
	selectedRoom: Room | undefined;
	isLoading: boolean;
}

const initialState = {
	rooms: [],
	selectedRoom: undefined,
	isLoading: false,
} as RoomState;

export const fetchRoomsThunk = createAsyncThunk<Room[], void>(
	'room/fetchRooms',
	async (_undefined, { rejectWithValue }) => {
		try {
			return await fetchRooms();
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const fetchRoomSearchFilterThunk = createAsyncThunk<
	Room[],
	{ params: RoomSearchFilterParam }
>('room/fetchRoomsSearchFilter', async ({ params }, { rejectWithValue }) => {
	try {
		return await fetchRoomsSearchFilter(params);
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const updateRoomThunk = createAsyncThunk<Room, { id: number; param: RoomParams }>(
	'room/updateRoom',
	async ({ id, param }, { rejectWithValue }) => {
		try {
			return await updateRoom(id, param);
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

const roomSlice = createSlice({
	name: 'room',
	initialState,
	reducers: {
		selectRoom: (state, action: PayloadAction<number>) => {
			state.selectedRoom = state.rooms[action.payload];
		},
		unselectRoom: (state) => {
			state.selectedRoom = undefined;
		},
		resetRoom: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchRoomsThunk.fulfilled, (state, action) => {
			const ret: { [id: number]: Room } = {};
			state.rooms = action.payload.reduce((obj, room) => {
				obj[room.id] = room;
				return obj;
			}, ret);
			state.isLoading = false;
		});
		builder.addCase(fetchRoomsThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchRoomsThunk.rejected, (state, action) => {
			state.rooms = [];
			state.isLoading = false;
		});

		builder.addCase(fetchRoomSearchFilterThunk.fulfilled, (state, action) => {
			const ret: { [id: number]: Room } = {};
			state.rooms = action.payload.reduce((obj, room) => {
				obj[room.id] = room;
				return obj;
			}, ret);
			state.isLoading = false;
		});
		builder.addCase(fetchRoomSearchFilterThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchRoomSearchFilterThunk.rejected, (state, action) => {
			state.rooms = [];
			state.isLoading = false;
		});

		builder.addCase(updateRoomThunk.fulfilled, (state, action) => {
			state.rooms[action.payload.id] = action.payload;
			state.isLoading = false;
		});
		builder.addCase(updateRoomThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(updateRoomThunk.rejected, (state) => {
			state.isLoading = false;
		});
	},
});

export const { selectRoom, unselectRoom, resetRoom } = roomSlice.actions;

export default roomSlice;
